import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import {
  stylesOfAssetType,
  styleTakesWaveform,
  waveformsOfAssetType,
  type AssetStyleId,
  type WaveformShape,
} from 'fym-shared/src/music24/assetStyles';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../components/primitives/Panel';
import { useVideoCredits } from '../credits/useVideoCredits';
import { videoCreditsLeft } from '../credits/videoCreditGate';
import { StudioActionError } from '../studio/StudioActionError';
import { fileLostAfterNext, nextRevisionNumber, RevisionEntry } from './revisionModel';
import { revisionCost } from './revisionMakeItem';
import { revisionMessages } from './revisionMessages';
import { NewRevisionFields } from './NewRevisionFields';
import { useMakeRevision } from './useMakeRevision';
/* eslint-disable css-modules/no-unused-class -- the viewer buttons */
import viewerStyles from '../viewer/AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface NewRevisionDialogProps {
  accountId: number;
  catalogItemUuid: string;
  songTitle: string;
  /** "Story (Out now)". */
  assetLabel: string;
  /** The revision it starts from: its settings fill the fields. */
  entry: RevisionEntry;
  entries: RevisionEntry[];
  /** The theme of the song now: a new revision always uses it. */
  songThemeId: ReleaseThemeId;
  onClose: () => void;
  /** After a made revision: the number of the new revision. */
  onMade: (number: number) => void;
  /** "More options in the Studio": the Studio on this asset type with these settings. */
  onMoreOptions: () => void;
}

/**
 * "Make a new revision" (design d3): starts with the settings of the open revision. "Make rN" calls
 * the existing make call with the same type + variant; the card shows rN as working at once.
 */
const NewRevisionDialogComponent: React.FC<NewRevisionDialogProps> = (props) => {
  const { accountId, catalogItemUuid, songTitle, assetLabel, entry, entries, songThemeId } = props;
  const { onClose, onMade, onMoreOptions } = props;
  const intl = useIntl();
  const number = useMemo(() => nextRevisionNumber(entries), [entries]);
  const lost = useMemo(() => fileLostAfterNext(entries), [entries]);
  const [style, setStyle] = useState<AssetStyleId | undefined>(entry.style ?? undefined);
  const [waveform, setWaveform] = useState<WaveformShape | undefined>(entry.waveform ?? undefined);
  const [text, setText] = useState<string | null>(entry.asset?.params.lyricLine ?? null);
  const styleIds = useMemo(() => (entry.kind === 'reel' ? [] : stylesOfAssetType(entry.themeId)), [entry]);
  const waveforms = useMemo(() => {
    if (entry.kind === 'reel') {
      return waveformsOfAssetType('aiReel');
    }
    return style !== undefined && styleTakesWaveform(entry.themeId, style) ? waveformsOfAssetType(entry.themeId) : [];
  }, [entry, style]);
  const choice = useMemo(() => ({ style, waveform, text: text ?? undefined }), [style, text, waveform]);
  const cost = useMemo(() => revisionCost(entry, choice), [choice, entry]);
  const { credits } = useVideoCredits({ accountId });
  const left = videoCreditsLeft(credits);
  const made = useCallback(() => onMade(number), [number, onMade]);
  const { make, isMaking, feedback, clearFeedback } = useMakeRevision({ accountId, catalogItemUuid, onMade: made });
  const submit = useCallback(() => {
    make(entry, choice).catch(console.error);
  }, [choice, entry, make]);
  const change = useCallback(
    <T,>(set: (value: T) => void) =>
      (value: T) => {
        clearFeedback();
        set(value);
      },
    [clearFeedback]
  );

  return (
    <div className={styles.layer}>
      <Panel
        className={styles.dialogBox}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-revision-title"
        data-testid="new-revision-dialog"
      >
        <PanelHead
          titleId="new-revision-title"
          title={intl.formatMessage(revisionMessages.dialogTitle)}
          subtitle={intl.formatMessage(revisionMessages.dialogSub, { name: assetLabel, title: songTitle, number })}
        />
        <PanelBody className="d-flex flex-column gap-3">
          <NewRevisionFields
            songThemeId={songThemeId}
            styleIds={styleIds}
            waveforms={waveforms}
            style={style}
            waveform={waveform}
            text={text}
            onStyle={change(setStyle)}
            onWaveform={change(setWaveform)}
            onText={change(setText)}
          />
          <span className={styles.infoLine} data-testid="new-revision-cost">
            {cost === 0
              ? intl.formatMessage(revisionMessages.costFree)
              : [
                  intl.formatMessage(revisionMessages.costCredits, { cost }),
                  left === null ? null : intl.formatMessage(revisionMessages.costLeft, { left }),
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
          </span>
          <span className={styles.infoLine}>
            {lost === null
              ? intl.formatMessage(revisionMessages.keepNote)
              : intl.formatMessage(revisionMessages.keepNoteLoses, { next: number, lost: lost.number })}
          </span>
          <StudioActionError feedback={feedback} />
        </PanelBody>
        <PanelFoot className={styles.footer}>
          <button type="button" className={clsx(viewerStyles.btn, viewerStyles.btnLink)} onClick={onMoreOptions}>
            {intl.formatMessage(revisionMessages.moreOptions)}
          </button>
          <span className={styles.grow} />
          <button type="button" className={clsx(viewerStyles.btn, viewerStyles.btnGhost)} onClick={onClose}>
            {intl.formatMessage(revisionMessages.cancel)}
          </button>
          <button
            type="button"
            className={clsx(viewerStyles.btn, viewerStyles.btnPrimary)}
            disabled={isMaking}
            onClick={submit}
            data-testid="new-revision-make"
          >
            {intl.formatMessage(revisionMessages.make, { number })}
          </button>
        </PanelFoot>
      </Panel>
    </div>
  );
};

export const NewRevisionDialog = TypedMemo(NewRevisionDialogComponent);
